/**
 * The task board as a Pinned tile shows it (Part 09 decision 3, mockup
 * `boardPin`): the board's real list rows, grouped by status in urgency
 * order with a `Status · count` heading. Done stays in the full board. No
 * drag and drop and no view modes: "Open board" has those. While the wall
 * searches it shows the matching tasks of every status, done included,
 * their matched text marked. Like everything on the wall it never scrolls:
 * it shows what fits, fades at the tile's foot, and "Open board" has the
 * rest.
 */
import { SurfaceFade } from "@/components/ui";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { ListViewRow } from "./list-view-row";
import { TASK_STATUS_LABEL_KEYS } from "./task-metadata-chips";
import type { TaskboardStatusGroup } from "./taskboard-status-groups";

export function TaskboardStatusList({
  groups,
  query,
  onSelectTask,
}: {
  /** `taskboardStatusGroups` of the board's tasks. */
  groups: readonly TaskboardStatusGroup[];
  /** The trimmed wall search; empty at rest. */
  query: string;
  onSelectTask: (taskId: string) => void;
}) {
  const t = useT(framesTaskboardMessages);
  if (groups.length === 0) {
    return (
      <p
        className="px-[1.2em] pt-[0.9em] text-appkit-body text-muted"
        data-testid="taskboard-status-list-empty"
      >
        {query ? t("noTasksMatch", { query }) : t("noOpenTasks")}
      </p>
    );
  }
  return (
    <div
      className="nodrag relative min-h-0 flex-1 overflow-hidden px-[0.8em] pb-[0.8em] pt-[0.5em]"
      data-frame-content="true"
      data-testid="taskboard-status-list"
    >
      {groups.map((group) => (
        <section
          key={group.status}
          data-testid={`taskboard-status-group-${group.status}`}
        >
          <h5 className="mx-[0.4em] mb-[0.35em] mt-[0.6em] text-[0.78em] font-medium tracking-[0.02em] text-muted">
            {t(TASK_STATUS_LABEL_KEYS[group.status])} · {group.tasks.length}
          </h5>
          {group.tasks.map((task) => (
            <ListViewRow
              key={task.id}
              task={task}
              query={query}
              onSelect={onSelectTask}
            />
          ))}
        </section>
      ))}
      <SurfaceFade />
    </div>
  );
}
